import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import CommonForm from "Components/Common/CommonForm";
import { usePermission } from "context/PermissionContext";
import { GetUserData, showToastMessage } from "common/service/CommonService";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { ACCESSIONING_PATHS } from "Routes/paths";
import { useScanningSessionService } from "../api/useScanningSession";
import { getNewAccessioningFields } from "../helper/scanningconstant";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";

interface NewScanningSessionModalProps {
  isOpen: boolean;
  toggle: () => void;
  redirectPathPattern?: string;
  isNewSessionFlow: boolean;
  onViewSession?: (session: any) => void;
  defaultCategory?: "sample_collection" | "dosing";
}

type AccessioningFormValues = {
  studycode: string;
  period: string;
  timepoint: string;
  dosingStation?: string;
};

const NewScanningSessionModal: React.FC<NewScanningSessionModalProps> = ({
  isOpen,
  toggle,
  redirectPathPattern,
  isNewSessionFlow,
  onViewSession,
  defaultCategory = "sample_collection",
}) => {
  const [sessionCategory, setSessionCategory] = React.useState<"sample_collection" | "dosing">(defaultCategory);

  useEffect(() => {
    setSessionCategory(defaultCategory);
  }, [defaultCategory, isOpen]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AccessioningFormValues>({
    defaultValues: {
      studycode: "",
      period: "",
      timepoint: "",
      dosingStation: "",
    },
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const selectedStudyCode = watch("studycode");
  const selectedPeriod = watch("period");

  useEffect(() => {
    setValue("dosingStation", "");
    setValue("timepoint", "");
  }, [selectedPeriod, setValue]);

  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });
  const { hasPermission } = usePermission();
  const hasAllStudiesRight = hasPermission(PERMISSION_MODULES.GLOBAL_STUDIES, PERMISSION_ACTIONS.ALL_STUDIES);
  const user = GetUserData();

  const studyOptions: Array<{
    label: string | number;
    value: string | number;
  }> = hasAllStudiesRight
      ? (((studySelectAllQuery.data as any)?.data || []).map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })))
      : ((user?.studies as any[])?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })) || []);

  const { SelectQuery: studyDetailQuery } = useMasterStudyService({
    id: selectedStudyCode ? Number(selectedStudyCode) : null,
    enabledSelect: true,
  });

  const studyDetail = (studyDetailQuery.data as any)?.data?.[0];

  const periodOptions =
    studyDetail?.periods?.map((p: any) => ({
      label: `${p.period_number}`,
      value: p.period_id,
    })) || [];

  const timepointOptions =
    studyDetail?.samplingpoints?.map((tp: any) => ({
      label: tp,
      value: tp,
    })) || [];

  const selectedPeriodObj = studyDetail?.periods?.find(
    (p: any) => String(p.period_id) === String(selectedPeriod)
  );

  const subjectsPerStation = selectedPeriodObj?.subjectsperstation;

  const dosingStationOptions =
    subjectsPerStation && typeof subjectsPerStation === "object"
      ? Object.keys(subjectsPerStation)
        .sort((a, b) => {
          const numA = parseInt(a.replace(/\D/g, ""), 10) || 0;
          const numB = parseInt(b.replace(/\D/g, ""), 10) || 0;
          return numA - numB;
        })
        .map((key: string) => {
          const numMatch = key.match(/\d+/);
          const stationNum = numMatch ? numMatch[0].padStart(2, "0") : key;
          return {
            label: `Dosing Station ${stationNum}`,
            value: key,
          };
        })
      : [];

  const protocolFields = getNewAccessioningFields("protocol", {
    studyOptions,
  });

  const isDosing = sessionCategory === "dosing";

  const periodFields = getNewAccessioningFields("period", {
    selectedStudyCode,
    selectedPeriod,
    periodOptions,
    timepointOptions,
    sessionCategory,
    dosingStationOptions: [
      { label: dosingStationOptions.length > 0 ? "Select Dosing Station" : "No Dosing Stations Available", value: "" },
      ...dosingStationOptions
    ]
  });

  const navigate = useNavigate();
  const { InsertMutation, SelectAllQuery } = useScanningSessionService({
    studyid: selectedStudyCode ? Number(selectedStudyCode) : null,
    pagesize: null,
  });

  const onSubmit = (data: AccessioningFormValues) => {
    const dsVal = data.dosingStation || "";
    const stationNum = dsVal ? (parseInt(dsVal.replace(/\D/g, ""), 10) || dsVal) : null;

    const payload = {
      ...data,
      timepoint: isDosing ? "Dosing" : data.timepoint,
      sessiontype: sessionCategory,
      dosingstationno: stationNum,
      dosingStation: dsVal || undefined,
    };

    if (isNewSessionFlow) {
      if (data.studycode) {
        InsertMutation.mutate(payload, {
          onSuccess: (res: any) => {
            showToastMessage(
              "success",
              res?.message || `${isDosing ? "IP Dosing" : "Sample Collection"} session initialized successfully`,
            );
            const targetPath = (redirectPathPattern || ACCESSIONING_PATHS.SESSION).replace(
              ":id",
              res.data.sessionid,
            );
            toggle();
            navigate(isDosing ? `${targetPath}/dosing` : targetPath);
          },
          onError: (error: any) => {
            const errMsg =
              typeof error === "string" && error.trim().length > 0
                ? error
                : error?.response?.data?.message ||
                  error?.data?.message ||
                  error?.message ||
                  "Failed to initialize session";

            showToastMessage("error", errMsg);
          },
        });
      }
    } else {
      // Find matching session
      const selectedPeriodObj = studyDetail?.periods?.find(
        (p: any) => String(p.period_id) === String(data.period)
      );
      const selectedPeriodNumber = selectedPeriodObj ? selectedPeriodObj.period_number : null;

      const sessions = SelectAllQuery?.data?.data || [];
      const matchingSession = sessions.find((s: any) => {
        if (s.summarycard) return false;

        const isStudyMatch = Number(s.studyid) === Number(data.studycode);
        const isPeriodMatch =
          Number(s.period_number) === Number(selectedPeriodNumber) ||
          Number(s.periodid) === Number(data.period);

        const isTimepointMatch = isDosing ? true : String(s.timepoint_number) === String(data.timepoint);

        const isStationMatch =
          !s.dosingstationno ||
          Number(s.dosingstationno) === Number(stationNum);

        return isStudyMatch && isPeriodMatch && isTimepointMatch && isStationMatch;
      });

      if (matchingSession) {
        if (onViewSession) {
          onViewSession(matchingSession);
        }
      } else {
        showToastMessage("error", "No active session found for the selected parameters.");
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="fw-bold border-bottom p-3">
        <div className="d-flex align-items-center justify-content-between w-100 me-3">
          <div className="d-flex align-items-center gap-2">
            <i className={`ri-${isDosing ? "capsule-line text-success" : "scanner-2-line text-primary"} fs-20`}></i>
            <span className="d-block">
              {isNewSessionFlow
                ? isDosing
                  ? "New IP Dosing Session"
                  : "New Accessioning Session"
                : isDosing
                  ? "Select Existing Dosing Session"
                  : "Select Existing Session"}
            </span>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-4">
        <div>
          <div className="mb-3">
            <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "15px" }}>
              {isDosing ? "Dosing Session Parameters" : "Sample Collection Parameters"}
            </h6>
            <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
              {isDosing
                ? "Select study code, period, and dosing station for drug administration."
                : "Select the study details for the samples you are about to accession."}
            </p>
          </div>

          <div className="mb-3">
            <CommonForm
              fields={protocolFields}
              control={control}
              errors={errors}
              columns={1}
            />
          </div>
          <div className="mb-3">
            <CommonForm
              fields={periodFields}
              control={control}
              errors={errors}
              columns={2}
            />
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button color="secondary" outline onClick={toggle}>
          Cancel
        </Button>
        <Button
          color={isDosing ? "success" : "primary"}
          onClick={handleSubmit(onSubmit)}
          disabled={isNewSessionFlow ? InsertMutation.isPending : SelectAllQuery.isFetching}
        >
          {(isNewSessionFlow && InsertMutation.isPending) || (!isNewSessionFlow && SelectAllQuery.isFetching) ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            <i className="ri-play-line me-1"></i>
          )}
          {isNewSessionFlow ? "Initialize Session" : "View Session"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default NewScanningSessionModal;
